import React, { useState } from 'react';
import {
  DocumentTextIcon,
  BuildingLibraryIcon,
  CheckCircleIcon,
  ClockIcon,
  BellAlertIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import useDocumentMetadata from '../hooks/useDocumentMetadata';

const features = [
  {
    icon: DocumentTextIcon,
    title: 'Unified Application Form',
    description:
      'Fill out a single smart form once and apply to multiple universities without repeating yourself.',
  },
  {
    icon: BuildingLibraryIcon,
    title: 'Partner University Network',
    description:
      'Apply directly to our growing network of partner universities across Pakistan — all in one place.',
  },
  {
    icon: CheckCircleIcon,
    title: 'Real-Time Status Tracking',
    description:
      'Track the status of every application in a clean dashboard, from submitted to accepted.',
  },
  {
    icon: ArrowPathIcon,
    title: 'Smart Auto-Fill',
    description:
      'Your profile data is automatically used to pre-fill application fields, saving you hours of repetitive work.',
  },
];

const DirectApply = () => {
  useDocumentMetadata(
    'DirectApply — Unified University Applications | Dakhla',
    'One form. Multiple universities. Zero hassle. Apply directly to Pakistan\'s top institutions with Dakhla DirectApply.'
  );

  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const handleNotify = (e) => {
    e.preventDefault();
    if (email.trim()) {
      setSubmitted(true);
    }
  };

  return (
    <div className="min-h-screen bg-slate-100 pb-20">

      {/* Hero */}
      <div className="relative bg-[#1E256F] text-white py-24 overflow-hidden shadow-sm">
        {/* Subtle grid pattern overlay */}
        <div
          className="absolute top-0 left-0 w-full h-full pointer-events-none"
          style={{
            backgroundImage: "linear-gradient(to right, rgba(255, 255, 255, 0.07) 1px, transparent 1px), linear-gradient(to bottom, rgba(255, 255, 255, 0.07) 1px, transparent 1px)",
            backgroundSize: "24px 24px"
          }}
        />

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          {/* Coming Soon badge */}
          <span className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-white/15 border border-white/20 text-white text-xs font-bold uppercase tracking-wider mb-6 backdrop-blur-sm">
            <ClockIcon className="h-3.5 w-3.5" />
            Coming Soon
          </span>

          <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold tracking-tight leading-tight mb-6">
            DirectApply
          </h1>
          <p className="text-lg sm:text-xl text-primary-100 max-w-2xl mx-auto leading-relaxed">
            One form. Multiple universities. Zero hassle. Apply directly to
            Pakistan's top institutions from Dakhla.
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 space-y-16">

        {/* What Is It Card */}
        <div className="card p-8 md:p-12 text-center">
          <span className="text-xs font-bold tracking-wider text-primary-600 uppercase mb-2 block">
            About This Feature
          </span>
          <h2 className="text-3xl md:text-4xl font-extrabold text-slate-800 mb-6">
            What is DirectApply?
          </h2>
          <p className="text-base md:text-lg text-slate-600 max-w-3xl mx-auto leading-relaxed">
            DirectApply is Dakhla's unified application gateway. Instead of
            visiting each university website individually and filling out multiple admission forms, you fill out one
            comprehensive form and submit it directly to any of our partner
            universities with a single click. No duplicate data entry, no
            confusion, no wasted time.
          </p>
        </div>

        {/* Features Grid */}
        <div>
          <div className="text-center mb-10">
            <span className="text-xs font-bold tracking-wider text-primary-600 uppercase mb-2 block">
              Key Features
            </span>
            <h2 className="text-3xl font-extrabold text-slate-800">
              Everything in one place
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {features.map((f, i) => (
              <div
                key={i}
                className="card hover:-translate-y-1.5 hover:shadow-md hover:border-primary-200 transition-premium p-7 flex items-start gap-5"
              >
                <div className="flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-2xl bg-primary-50 text-primary-600 shadow-sm">
                  <f.icon className="h-6 w-6" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-slate-800 mb-2">{f.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{f.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Notify Me Card */}
        <div className="card bg-gradient-to-br from-primary-600 to-indigo-700 border-0 text-white p-8 md:p-12 text-center relative overflow-hidden">
          <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-white/5 blur-2xl pointer-events-none" />

          <div className="relative z-10">
            <div className="flex items-center justify-center h-14 w-14 rounded-2xl bg-white/15 border border-white/20 mx-auto mb-5">
              <BellAlertIcon className="h-7 w-7 text-white" />
            </div>
            <h2 className="text-2xl md:text-3xl font-extrabold mb-3">
              Be the first to know
            </h2>
            <p className="text-primary-100 mb-8 max-w-md mx-auto text-sm md:text-base">
              Direct Apply is launching soon. Drop your email and we'll notify
              you the moment it goes live.
            </p>

            {submitted ? (
              <div className="flex items-center justify-center gap-2 bg-white/15 border border-white/20 rounded-xl px-6 py-4 max-w-sm mx-auto">
                <CheckCircleIcon className="h-5 w-5 text-green-300 shrink-0" />
                <span className="text-sm font-semibold">
                  You're on the list — we'll be in touch!
                </span>
              </div>
            ) : (
              <form
                onSubmit={handleNotify}
                className="flex flex-col sm:flex-row items-center gap-3 max-w-md mx-auto"
              >
                <input
                  type="email"
                  id="direct-apply-notify-email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  required
                  className="flex-1 w-full px-4 py-3 rounded-xl bg-white/15 border border-white/25 text-white placeholder-primary-200 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-white/40 focus:bg-white/20 transition-all"
                />
                <button
                  type="submit"
                  id="direct-apply-notify-btn"
                  className="w-full sm:w-auto whitespace-nowrap px-6 py-3 bg-white text-primary-700 font-bold text-sm rounded-xl hover:bg-primary-50 active:scale-[0.98] transition-all duration-200 shadow-md"
                >
                  Notify Me
                </button>
              </form>
            )}
          </div>
        </div>

      </div>
    </div>
  );
};

export default DirectApply;
